<script setup lang="ts">
import { supportedLocales } from '@pkg/config/languages';
import { getIso2Digit } from '@pkg/utilities/i18n';
import { usePageVisibilityRobots } from '@pkg/sanity-utils/composables/usePageVisibilityRobots';
import { useSanityPageQuery } from '~/app/composables/useSanityPageQuery';
import { syncLayoutHideHeader } from '~/app/composables/useLayoutHideHeader';
import { useAboutUsJsonLd } from '~/app/composables/useAboutUsJsonLd';
import { useContactUsJsonLd } from '~/app/composables/useContactUsJsonLd';
import { useHomeJsonLd } from '~/app/composables/useHomeJsonLd';
import { useServiceJsonLd } from '~/app/composables/useServiceJsonLd';
import { useHowToJsonLd } from '~/app/composables/useHowToJsonLd';
import { useCmsPageBreadcrumbJsonLd } from '~/app/composables/useCmsPageBreadcrumbJsonLd';
import { usePaginationMetadata } from '~/app/composables/usePaginationMetadata';
import ContentRenderer from '~/app/features/cms/components/ContentRenderer.vue';
import type { PageContent } from '~/app/features/cms/types/PageContent';
import { useSanityPageSeoMeta } from '~/app/composables/useSanityPageSeoMeta';
import Page404 from '~/app/features/cms/components/Page404.vue';
import { useHrefLinks } from '~/app/composables/useHrefLinks';
import type { HrefLink, Translation } from '~/app/types/hreflinks';

// See: https://i18n.nuxtjs.org/guide/seo
const head = useLocaleHead({
  addDirAttribute: true,
  identifierAttribute: 'id',
  addSeoAttributes: true,
});

const route = useRoute();

const sanityResponse:any = await useSanityPageQuery<{ page?: PageContent | null }>();
const { data = ref(null) } = sanityResponse || {};
syncLayoutHideHeader(data);
if (data.value?.page?.seoSocial) {
  useSanityPageSeoMeta(data.value.page.seoSocial);
}
usePageVisibilityRobots(data.value?.page?.pageVisibility);

useAboutUsJsonLd({ pageData: data });
useContactUsJsonLd({ pageData: data });
useHomeJsonLd({ pageData: data });
useServiceJsonLd({ pageData: data });
useHowToJsonLd(data);
useCmsPageBreadcrumbJsonLd(data);

const isMounted = ref(false);

onMounted(() => {
  isMounted.value = true;
});

const { getLocalizedHref } = useHrefLinks(route);

// Handle pagination SEO metadata (must be defined before use)
const { addPaginationToUrl, generatePaginationSeoMeta } = usePaginationMetadata();

const hasPageTranslations = computed(() =>
  data?.value?.page?._translations?.length > 0,
);

// Filter head.links to only output those that we have a translation for or the canonical link
const isCanonicalLink = (link: HrefLink) => link.rel === 'canonical';

const filteredHeadLinks = computed(() => {
  return (head.value.link || []).filter(
    (link: HrefLink) =>
      isCanonicalLink(link) ||
      data?.value?.page?._translations?.some(
        (translation: Translation) => translation?.language === link.hreflang,
      ),
  );
});

// No canonical on 404s: it would point at the broken URL itself.
const renderLinkCondition = (link: HrefLink) =>
  isCanonicalLink(link) ? !!data?.value?.page : supportedLocales.includes(link.hreflang);

const getHref = (link: HrefLink) => {
  try {
    // Pages can opt out of the self-referencing canonical (e.g. surprise bags viewed under a non-home locale)
    if (isCanonicalLink(link) && data?.value?.page?.canonicalUrl) {
      return data.value.page.canonicalUrl;
    }
    if (hasPageTranslations.value) {
      const localizedHref = getLocalizedHref(link, data?.value?.page?._translations, data?.value?.page?._type);
      if (localizedHref) {
        return addPaginationToUrl(localizedHref);
      }
    }
    return addPaginationToUrl(link.href);
  } catch (e) {
    return LOCALE_CODE.EN_GB;
  }
};

const getHreflang = (link: HrefLink) => {
  try {
    if (isCanonicalLink(link)) { return; }

    const isHyphenated = link.hreflang?.includes('-');
    const countryString = getIso2Digit(link.hreflang).toUpperCase() || '';
    const languageString = isHyphenated ? link.hreflang?.substring(0, 2) : link.hreflang;
    return `${languageString}-${countryString.toUpperCase()}`;
  } catch (e) {
    return LOCALE_CODE.EN_GB;
  }
};

const filteredHeadMeta = computed(() => {
  return (head.value.meta || []).filter((meta: any) => {
    if (meta.property === 'og:locale:alternate') {
      const localeFromContent = meta.content?.replace('_', '-');
      const hasTranslation = data?.value?.page?._translations?.some(
        (translation: Translation) => translation?.language === localeFromContent,
      );
      return hasTranslation;
    }
    return true;
  }).map((meta: any) => {
    // Handle og:url meta tag with pagination parameters
    if (meta.property === 'og:url') {
      return { ...meta, content: data?.value?.page?.canonicalUrl || addPaginationToUrl(meta.content) };
    }
    return meta;
  });
});

// Override SEO metadata for pagination
watchEffect(() => {
  if (data.value?.page?.seoSocial?.metaTitle) {
    const seoMeta = generatePaginationSeoMeta(data.value.page.seoSocial.metaTitle);
    if (seoMeta) {
      useHead({ link: [{ rel: 'canonical', href: seoMeta.canonicalUrl }] });
      useSeoMeta({
        title: seoMeta.title,
        ogTitle: seoMeta.ogTitle,
        ogUrl: seoMeta.ogUrl,
      });
    }
  }
});
</script>

<template>
  <Html :lang="head.htmlAttrs?.lang || 'en'" :dir="head.htmlAttrs?.dir">
    <Head>
      <template v-for="link in filteredHeadLinks" :key="link.id">
        <Link
          v-if="renderLinkCondition(link)"
          :id="link.id"
          :rel="link.rel"
          :href="getHref(link)"
          :hreflang="getHreflang(link)"
        />
      </template>
      <template v-for="meta in filteredHeadMeta" :key="meta.id">
        <Meta :id="meta.id" :property="meta.property" :content="meta.content" />
      </template>
    </Head>
  </Html>
  <ContentRenderer v-if="data?.page" :content="data?.page" />
  <Page404 v-else />
</template>
